import { z } from 'zod';

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'expected a 6-digit hex colour like #1A2B3C');

// Free-form CSS values (overlay colour, shadow) end up inside a stylesheet, so no CSS syntax
// that could close a rule or a <style> element.
const cssValue = z
  .string()
  .regex(/^[A-Za-z0-9#%.,/()\s+-]+$/, 'expected a plain CSS value (no quotes, braces or ;)');

// Font family names end up inside a quoted CSS string, so they cannot hold quotes or CSS syntax.
const fontFamily = z
  .string()
  .regex(/^[^"'\\;{}<>\r\n]+$/, 'expected a plain font family name (no quotes or CSS syntax)');

const px = z.number().nonnegative();

/** Every colour of one mode. Keys map 1:1 to the `--bl-*` variables (see `colorVarName`). */
const palette = z.object({
  canvas: hex,
  surface: hex,
  surfaceMuted: hex,
  line: hex,
  lineStrong: hex,
  ink: hex,
  inkMuted: hex,
  inkSubtle: hex,
  primary: hex,
  primaryHover: hex,
  primarySoft: hex,
  onPrimary: hex,
  accent: hex,
  accentSoft: hex,
  danger: hex,
  dangerSoft: hex,
  warning: hex,
  warningSoft: hex,
  success: hex,
  successSoft: hex,
  info: hex,
  infoSoft: hex,
  sidebar: hex,
  sidebarInk: hex,
  sidebarInkStrong: hex,
  sidebarActive: hex,
  sidebarLine: hex,
  chart1: hex,
  chart2: hex,
  chart3: hex,
  /** Scrim behind dialogs and drawers (may carry alpha, so not a hex). */
  overlay: cssValue,
  /** `box-shadow` value for floating surfaces. */
  shadowOverlay: cssValue,
});

/** Zod schema of a design-token set: every key is required except `direction`. */
export const tokensSchema = z.object({
  name: z.string().min(1),
  /** Layout direction of the product. */
  direction: z.enum(['rtl', 'ltr']).default('rtl'),
  color: z.object({ light: palette, dark: palette }),
  font: z.object({ arabic: fontFamily, latin: fontFamily }),
  size: z.object({ xs: px, sm: px, md: px, lg: px, xl: px, '2xl': px }),
  lineHeight: z.object({ tight: z.number().positive(), normal: z.number().positive() }),
  radius: z.object({ sm: px, md: px, lg: px, xl: px, full: px }),
  space: z.array(px).length(8),
});

export type Tokens = z.infer<typeof tokensSchema>;
export type TokensInput = z.input<typeof tokensSchema>;
export type Mode = keyof Tokens['color'];
export type Palette = Tokens['color'][Mode];
export type ColorKey = keyof Palette;

/** Parse unknown data into `Tokens`; the error message names every bad key by its dotted path. */
export function parseTokens(input: unknown): Tokens {
  const result = tokensSchema.safeParse(input);
  if (result.success) return result.data;
  const lines = result.error.issues.map(
    (issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`,
  );
  throw new Error(`invalid tokens\n${lines.join('\n')}`);
}

/** Define a token set: validated at call time, so a bad product token set fails at import. */
export function defineTokens(input: TokensInput): Tokens {
  return parseTokens(input);
}
